import type { GeneratedIntakeField, GeneratedIntakeItem, IntakeFieldValue } from './types';

export interface IntakeFieldState {
  acknowledgedValue: IntakeFieldValue;
  draftValue: IntakeFieldValue;
  hasAcknowledgedValue: boolean;
}

export type IntakeFieldStates = Record<string, IntakeFieldState>;

const SAME_AS_PRACTICE_SUFFIX = '_same_as_practice';
const AFFIRMATIVE_ACKNOWLEDGEMENT_KEYS = new Set(['mailing_address_public_ack']);

export function acknowledgePersistedFieldValue(
  previous: IntakeFieldStates,
  fieldId: string,
  persistedValue: IntakeFieldValue,
  acknowledgedValue: IntakeFieldValue = persistedValue,
): IntakeFieldStates {
  const existing = previous[fieldId];
  return {
    ...previous,
    [fieldId]: {
      acknowledgedValue,
      draftValue:
        existing && !intakeFieldValuesEqual(existing.draftValue, persistedValue)
          ? existing.draftValue
          : acknowledgedValue,
      hasAcknowledgedValue: true,
    },
  };
}

export function replaceFieldValueWithAcceptedSuggestion(
  previous: IntakeFieldStates,
  fieldId: string,
  acceptedValue: IntakeFieldValue,
): IntakeFieldStates {
  return {
    ...previous,
    [fieldId]: {
      acknowledgedValue: acceptedValue,
      draftValue: acceptedValue,
      hasAcknowledgedValue: true,
    },
  };
}

export function defaultFieldValue(field: GeneratedIntakeField): IntakeFieldValue {
  if (field.data_type === 'boolean') return false;
  if (field.data_type === 'multi-enum' || field.data_type === 'multi-string') return [];
  return '';
}

export function sameAsPracticeParentKey(field: GeneratedIntakeField): string | null {
  if (
    field.data_type.toLowerCase() !== 'boolean' ||
    field.destination.kind !== 'intake_item_evidence' ||
    !field.destination.field_key.endsWith(SAME_AS_PRACTICE_SUFFIX)
  ) {
    return null;
  }

  return field.destination.field_key.slice(0, -SAME_AS_PRACTICE_SUFFIX.length) || null;
}

export function requiresAffirmativeAcknowledgement(field: GeneratedIntakeField): boolean {
  return (
    field.is_required &&
    field.destination.kind === 'intake_item_evidence' &&
    AFFIRMATIVE_ACKNOWLEDGEMENT_KEYS.has(field.destination.field_key)
  );
}

export function updateFieldStatesForDraftChange(
  fields: GeneratedIntakeField[],
  previous: IntakeFieldStates,
  field: GeneratedIntakeField,
  value: IntakeFieldValue,
): IntakeFieldStates {
  const next: IntakeFieldStates = {
    ...previous,
    [field.field_id]: {
      acknowledgedValue: previous[field.field_id]?.acknowledgedValue ?? defaultFieldValue(field),
      draftValue: value,
      hasAcknowledgedValue: previous[field.field_id]?.hasAcknowledgedValue ?? false,
    },
  };
  const derivedParentKey = sameAsPracticeParentKey(field);
  if (!derivedParentKey) return next;

  for (const dependentField of fields) {
    if (dependentField.parent_field_key !== derivedParentKey) continue;
    const emptyValue = defaultFieldValue(dependentField);
    next[dependentField.field_id] = {
      acknowledgedValue: emptyValue,
      draftValue: emptyValue,
      hasAcknowledgedValue: false,
    };
  }
  return next;
}

export function isIntakeFieldValue(value: unknown): value is IntakeFieldValue {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    (Array.isArray(value) && value.every(entry => typeof entry === 'string'))
  );
}

export function intakeFieldValuesEqual(left: IntakeFieldValue, right: IntakeFieldValue): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }
  return Object.is(left, right);
}

function hydratedValue(
  field: GeneratedIntakeField,
  items: GeneratedIntakeItem[],
  currentUserId?: string | null,
): { present: boolean; value: IntakeFieldValue } {
  if (field.destination.kind === 'intake_item_sensitive') {
    const item = findIntakeItemForField(field, items, currentUserId);
    const present = Boolean(item?.sensitive_fields_on_file?.includes(field.destination.field_key));
    return { present, value: present ? true : defaultFieldValue(field) };
  }
  if (field.destination.kind !== 'intake_item_evidence' || !field.requirement_key) {
    return { present: false, value: defaultFieldValue(field) };
  }

  const item = findIntakeItemForField(field, items, currentUserId);
  const extractedFields = item?.evidence_summary?.extracted_fields;
  const present = Boolean(
    extractedFields &&
    Object.prototype.hasOwnProperty.call(extractedFields, field.destination.field_key),
  );
  const value: unknown = extractedFields?.[field.destination.field_key];
  return {
    present: present && isIntakeFieldValue(value),
    value: isIntakeFieldValue(value) ? value : defaultFieldValue(field),
  };
}

export function hydrateFieldStates(
  fields: GeneratedIntakeField[],
  items: GeneratedIntakeItem[],
  previous: IntakeFieldStates,
  currentUserId?: string | null,
): IntakeFieldStates {
  let next = previous;
  for (const field of fields) {
    const serverState = hydratedValue(field, items, currentUserId);
    const existing = previous[field.field_id];
    const hydrated: IntakeFieldState = {
      acknowledgedValue: serverState.value,
      draftValue:
        existing && !intakeFieldValuesEqual(existing.draftValue, existing.acknowledgedValue)
          ? existing.draftValue
          : serverState.value,
      hasAcknowledgedValue: serverState.present,
    };

    if (
      existing &&
      intakeFieldValuesEqual(existing.acknowledgedValue, hydrated.acknowledgedValue) &&
      intakeFieldValuesEqual(existing.draftValue, hydrated.draftValue) &&
      existing.hasAcknowledgedValue === hydrated.hasAcknowledgedValue
    ) {
      continue;
    }

    if (next === previous) next = { ...previous };
    next[field.field_id] = hydrated;
  }
  return next;
}

export function findIntakeItemForField(
  field: GeneratedIntakeField,
  items: GeneratedIntakeItem[],
  currentUserId?: string | null,
): GeneratedIntakeItem | undefined {
  return items.find(item => {
    if (item.requirement_key !== field.requirement_key) return false;
    if (item.requirement_key === 'jotbill.payer_roster') {
      return field.view === 'clinic' && item.user_id == null;
    }
    if (field.view === 'clinic') {
      return Boolean(currentUserId) && item.user_id === currentUserId;
    }
    if (field.view === 'individual') {
      return Boolean(currentUserId) && item.user_id === currentUserId;
    }
    return false;
  });
}

export function hasDurableValue(value: IntakeFieldValue, hasAcknowledgedValue = true): boolean {
  if (typeof value === 'boolean') return hasAcknowledgedValue;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  return value.trim().length > 0;
}

export function durableRequiredCompletion(
  fields: GeneratedIntakeField[],
  states: IntakeFieldStates,
  items: GeneratedIntakeItem[] = [],
  currentUserId?: string | null,
): { complete: number; percent: number; total: number } {
  const derivedAddressParentKeys = new Set(
    fields.flatMap(field => {
      const parentKey = sameAsPracticeParentKey(field);
      const state = states[field.field_id];
      return parentKey && state?.acknowledgedValue === true && state.hasAcknowledgedValue
        ? [parentKey]
        : [];
    }),
  );
  const required = fields.filter(
    field =>
      field.is_required &&
      field.is_writable !== false &&
      (!field.repeat_scope || field.repeat_scope === 'none') &&
      // Only destinations this surface can hydrate and persist count toward
      // completion. jotcred_basic_info fields render read-only here (no
      // hydration, and handlePersistField rejects them), so counting them
      // would make 100% unreachable.
      (field.destination.kind === 'intake_item_evidence' ||
        field.destination.kind === 'intake_item_sensitive'),
  );
  const completeScalars = required.filter(field => {
    if (derivedAddressParentKeys.has(field.parent_field_key)) return true;
    const state = states[field.field_id];
    const value = state?.acknowledgedValue ?? defaultFieldValue(field);
    if (requiresAffirmativeAcknowledgement(field)) {
      return value === true && (state?.hasAcknowledgedValue ?? false);
    }
    return hasDurableValue(value, state?.hasAcknowledgedValue ?? false);
  }).length;
  const repeatedGroups = new Map<string, GeneratedIntakeField>();
  for (const field of fields) {
    if (
      !field.is_required ||
      field.is_writable === false ||
      !field.repeat_scope ||
      field.repeat_scope === 'none' ||
      !field.requirement_key
    ) {
      continue;
    }
    repeatedGroups.set(`${field.view}:${field.requirement_key}:${field.group_id}`, field);
  }
  const completeRepeated = [...repeatedGroups.values()].filter(field =>
    Boolean(findIntakeItemForField(field, items, currentUserId)?.is_complete),
  ).length;
  const complete = completeScalars + completeRepeated;
  const total = required.length + repeatedGroups.size;
  return {
    complete,
    total,
    percent: total === 0 ? 0 : Math.round((complete / total) * 100),
  };
}
